import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";

import { useLessons } from "../contexts/LessonsContext";
import { useSearch } from "../contexts/SearchContext";
import { useUser } from "../contexts/UserContext";
import { useCart } from "../contexts/CartContext";

import DateRangePicker from "../components/ui/DateRangePicker";
import LessonCard from "../components/lessons/LessonCard";
import CreateLessonModal from "../components/modals/CreateLessonModal";
import LessonParticipantsReportModal from "../components/modals/LessonParticipantsReportModal";
import ConfirmDialog from "../components/modals/ConfirmDialog";
import CourseInfo from "../components/common/CourseInfo";
import OnlineClassHeader from "../components/ui/OnlineClassHeader";
import RecordingsPlaylistModal from "../components/modals/RecordingsPlaylistModal";
import Pagination from "../components/ui/Pagination";
import SpinnerOverlay from "../components/ui/SpinnerOverlay";

const Tab = { TODAY: "TODAY", FUTURE: "FUTURE", HISTORY: "HISTORY" };
const MOBILE_PAGE_LIMIT = 4;
const DESKTOP_PAGE_LIMIT = 8;

const TAB_LIST = [
  { key: Tab.TODAY, label: "היום" },
  { key: Tab.FUTURE, label: "עתידיים" },
  { key: Tab.HISTORY, label: "עבר" },
];

const EMPTY_MSG = {
  [Tab.TODAY]: "אין מפגשים להיום",
  [Tab.FUTURE]: "אין מפגשים עתידיים",
  [Tab.HISTORY]: "אין מפגשים בארכיון",
};

const buildTeacherFromCourse = (c) => {
  if (!c || typeof c !== "object") return null;

  const t =
    (c.teacher && typeof c.teacher === "object" ? c.teacher : null) ||
    (c.Teacher && typeof c.Teacher === "object" ? c.Teacher : null) ||
    null;

  const first =
    (t?.firstName || t?.first_name || "") ||
    (c.teacherFirstName || c.teacher_first_name || "");

  const last =
    (t?.lastName || t?.last_name || "") ||
    (c.teacherLastName || c.teacher_last_name || "");

  const name =
    t?.name ||
    c.teacherName ||
    c.teacher_name ||
    `${first} ${last}`.trim();

  const pictureUrl =
    t?.pictureUrl ||
    t?.picture_url ||
    t?.image ||
    t?.avatar ||
    t?.profileImage ||
    t?.profile_image ||
    t?.photo ||
    c.teacherPictureUrl ||
    c.teacher_picture_url ||
    c.teacherImage ||
    c.teacher_image ||
    c.teacherAvatar ||
    c.teacher_avatar ||
    null;

  const id =
    t?.id ??
    t?.teacherId ??
    t?.teacher_id ??
    c.teacherId ??
    c.teacher_id ??
    null;

  if (!(name || pictureUrl || id || first || last)) return null;

  return {
    id,
    firstName: first || "",
    lastName: last || "",
    name: name || "מורה",
    pictureUrl,
  };
};


export default function OnlineClass() {
  const params = useParams();
  const courseId = params.courseId ?? params.id ?? null;

  const { courses, getMyCoursesActive, getMyCoursesArchived } = useSearch();
  
  const { role: userRole, userType, user, showAlert, ready, isLogedin } = useUser();

  const {
    canJoin,
    getRuntimeState,
    cancelLesson,
    fetchSavedLessonRecordings,
    markLessonRecordingWatched,
    fetchMyLessonsPage,
    fetchCourseLessonsPage,
  } = useLessons();

  const { isInCart, addLessonFromOnline } = useCart();

  const allCourses = useMemo(() => {
    const arrSearch = Array.isArray(courses) ? courses : [];
    const active = typeof getMyCoursesActive === "function" ? getMyCoursesActive() : [];
    const archived = typeof getMyCoursesArchived === "function" ? getMyCoursesArchived() : [];
    const arrMine = [
      ...(Array.isArray(active) ? active : []),
      ...(Array.isArray(archived) ? archived : []),
    ];

    const byId = new Map();

    for (const c of arrSearch) {
      if (c?.id != null) byId.set(String(c.id), c);
    }
    for (const c of arrMine) {
      if (c?.id != null) byId.set(String(c.id), c);
    }

    return Array.from(byId.values());
  }, [courses, getMyCoursesActive, getMyCoursesArchived]);

  const effectiveRole =
    userRole === "teacher" || userType === "teacher" ? "teacher" : "student";

  const isTeacher = effectiveRole === "teacher";
  const isTeacherApproved =
    isTeacher && String(user?.teacher_status || "").toUpperCase() === "APPROVED";

  // A supplier viewing their own course's lessons — attends like a
  // student (same LessonCard, same canJoin contract downstream) but
  // never needs to pay for it, since they own the course. Kept as a
  // separate flag rather than a third `effectiveRole` value, since
  // canJoin/LessonCard's contracts for a role beyond 'teacher'/'student'
  // aren't something this file can verify.
  const isSupplier = userRole === "supplier" || userType === "supplier";

  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingLesson, setEditingLesson] = useState(null);

  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportLesson, setReportLesson] = useState(null);

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [lessonToCancel, setLessonToCancel] = useState(null);

  const [active, setActive] = useState(Tab.TODAY);
  const [ranges, setRanges] = useState({
    [Tab.TODAY]: { start: "", end: "" },
    [Tab.FUTURE]: { start: "", end: "" },
    [Tab.HISTORY]: { start: "", end: "" },
  });

  const [recordingsModalOpen, setRecordingsModalOpen] = useState(false);
  const [selectedLessonTitle, setSelectedLessonTitle] = useState("");
  const [selectedRecordings, setSelectedRecordings] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 1024
      ? MOBILE_PAGE_LIMIT
      : DESKTOP_PAGE_LIMIT
  );
  const [pageItems, setPageItems] = useState([]);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);

  const [counts, setCounts] = useState({
    [Tab.TODAY]: 0,
    [Tab.FUTURE]: 0,
    [Tab.HISTORY]: 0,
  });

  const [reloadKey, setReloadKey] = useState(0);

  const reloadPage = useCallback(() => {
    setReloadKey((v) => v + 1);
  }, []);

  // ── Focus the lesson a teacher just created ──────────────────────────
  // handleLessonModalSuccess (create mode) stashes the ids visible right
  // now; once the reload that follows finishes, whichever lesson is new
  // (or the id the modal handed back, if it does) gets scrolled into
  // view, focused and briefly highlighted.
  const pendingFocusRef = useRef(null); // { prevIds:Set, explicitId, started:boolean }
  const [focusLessonId, setFocusLessonId] = useState(null);

  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending) return;

    if (pageLoading) {
      pending.started = true;
      return;
    }
    if (!pending.started) return; // reload hasn't begun yet — old items

    pendingFocusRef.current = null;
    const items = Array.isArray(pageItems) ? pageItems : [];
    const found =
      (pending.explicitId != null &&
        items.find((l) => String(l?.id) === String(pending.explicitId))) ||
      items.find((l) => l?.id != null && !pending.prevIds.has(String(l.id)));
    if (found) setFocusLessonId(found.id);
  }, [pageItems, pageLoading]);

  useEffect(() => {
    if (focusLessonId == null) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(`lesson-card-${focusLessonId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        try { el.focus({ preventScroll: true }); } catch { /* ignore */ }
      }
    });
    const t = setTimeout(() => setFocusLessonId(null), 3000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [focusLessonId]);

  const setRange = (key, s, e) =>
    setRanges((r) => ({
      ...r,
      [key]: { start: s || "", end: e || "" },
    }));

  const clearRange = (key) =>
    setRanges((r) => ({
      ...r,
      [key]: { start: "", end: "" },
    }));

  const course = useMemo(
    () =>
      courseId
        ? (allCourses || []).find((c) => String(c.id) === String(courseId)) ?? null
        : null,
    [allCourses, courseId]
  );

  const rawCourseType = String(course?.type || "").toLowerCase().trim();
  const isGroupCourse = rawCourseType === "group" || rawCourseType === "קבוצתי";
  const canShowCreateButton = !!courseId && isTeacher && isGroupCourse;
  const isCourseOpened = String(course?.status || "").trim().toLowerCase() === "opened";
  const canCreateLesson = canShowCreateButton && isTeacherApproved && isCourseOpened;

  const listForTab = pageItems;
  const totalPages = Math.max(1, Math.ceil((pageTotal || 0) / (pageSize || 1)));
  const hasRange = !!(ranges[active]?.start || ranges[active]?.end);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!pageLoading) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  }, [page, pageLoading]);

  useEffect(() => {
    const handleResize = () => {
      const nextSize =
        window.innerWidth < 1024 ? MOBILE_PAGE_LIMIT : DESKTOP_PAGE_LIMIT;
      setPageSize((prev) => (prev === nextSize ? prev : nextSize));
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set by locateCreatedLesson() when it switches tab/range AND already
  // knows which page the new lesson is on, so this reset doesn't clobber it.
  const pendingPageRef = useRef(null);

  useEffect(() => {
    const target = pendingPageRef.current;
    pendingPageRef.current = null;
    setPage(target || 1);
  }, [active, pageSize, ranges[active]?.start, ranges[active]?.end, courseId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (!ready || !isLogedin || !user) {
          setPageItems([]);
          setPageTotal(0);
          return;
        }

        setPageLoading(true);

        const scopeMap = {
          [Tab.TODAY]: "today",
          [Tab.FUTURE]: "future",
          [Tab.HISTORY]: "history",
        };

        const currentRange = ranges[active] || {};
        const payload = {
          scope: scopeMap[active],
          start: currentRange.start || "",
          end: currentRange.end || "",
          page,
          limit: pageSize,
        };

        const result = courseId
          ? await fetchCourseLessonsPage(courseId, payload)
          : await fetchMyLessonsPage(payload);

        if (cancelled) return;

        setPageItems(Array.isArray(result?.items) ? result.items : []);
        setPageTotal(Number(result?.total || 0));
      } catch (err) {
        if (cancelled) return;
        showAlert?.(err?.message || "שגיאה בטעינת המפגשים.", "error");
        setPageItems([]);
        setPageTotal(0);
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    active,
    page,
    pageSize,
    courseId,
    ranges,
    fetchMyLessonsPage,
    fetchCourseLessonsPage,
    showAlert,
    reloadKey,
    ready,
    isLogedin,
    user,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadCounts = async () => {
      try {

        if (!ready || !isLogedin || !user) {
          setCounts({
            [Tab.TODAY]: 0,
            [Tab.FUTURE]: 0,
            [Tab.HISTORY]: 0,
          });
          return;
        }
        
        const currentRange = ranges[active] || {};

        const commonPayload = {
          start: currentRange.start || "",
          end: currentRange.end || "",
          page: 1,
          limit: 1,
        };

        const fetchByScope = async (scope) => {
          const payload = { ...commonPayload, scope };
          const result = courseId
            ? await fetchCourseLessonsPage(courseId, payload)
            : await fetchMyLessonsPage(payload);

          return Number(result?.total || 0);
        };

        const [todayCount, futureCount, historyCount] = await Promise.all([
          fetchByScope("today"),
          fetchByScope("future"),
          fetchByScope("history"),
        ]);

        if (cancelled) return;

        setCounts({
          [Tab.TODAY]: todayCount,
          [Tab.FUTURE]: futureCount,
          [Tab.HISTORY]: historyCount,
        });
      } catch (err) {
        if (cancelled) return;
        console.warn("Failed to load online class counts", err);
      }
    };

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, [
    courseId,
    active,
    ranges[active]?.start,
    ranges[active]?.end,
    fetchMyLessonsPage,
    fetchCourseLessonsPage,
    reloadKey,
  ]);

  const handleRequestEdit = (lesson) => {
    setModalMode("edit");
    setEditingLesson(lesson);
    setIsLessonModalOpen(true);
  };

  const handleShowParticipants = (lesson, courseForLesson) => {
    setReportLesson({
      ...lesson,
      courseTitle: courseForLesson?.title || course?.title || "",
    });
    setIsReportOpen(true);
  };

  const handleCancelSession = (lesson) => {
    setLessonToCancel(lesson);
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!lessonToCancel) return;

    try {
      await cancelLesson(lessonToCancel.id);
      reloadPage();
      showAlert?.("המפגש בוטל בהצלחה.", "success");
    } catch (err) {
      showAlert?.(err?.message || "אירעה שגיאה בעת ביטול המפגש.", "error");
    } finally {
      setIsCancelDialogOpen(false);
      setLessonToCancel(null);
    }
  };

  const handleLessonModalClose = () => {
    setIsLessonModalOpen(false);
    setEditingLesson(null);
    setModalMode("create");
  };

  // Finds which tab (today / future / history) and which page the freshly
  // created lesson lands on, and navigates there, so it can be focused even
  // when it isn't on the page the teacher was looking at.
  const locateCreatedLesson = async (created) => {
    const id = created?.id ?? created?.lesson?.id ?? created?.lessonId ?? null;
    const date = String(created?.date ?? created?.lesson?.date ?? "").slice(0, 10);
    if (id == null || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return { id };

    const n = new Date();
    const todayStr = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
    const tab = date === todayStr ? Tab.TODAY : date > todayStr ? Tab.FUTURE : Tab.HISTORY;
    const scopeMap = { [Tab.TODAY]: "today", [Tab.FUTURE]: "future", [Tab.HISTORY]: "history" };

    // A date filter that would hide the new lesson is cleared for that tab.
    const r = ranges[tab] || {};
    const hidden = (r.start && date < r.start) || (r.end && date > r.end);
    const start = hidden ? "" : r.start || "";
    const end = hidden ? "" : r.end || "";

    let foundPage = null;
    try {
      for (let p = 1; p <= 30; p += 1) {
        const payload = { scope: scopeMap[tab], start, end, page: p, limit: pageSize };
        const result = courseId
          ? await fetchCourseLessonsPage(courseId, payload)
          : await fetchMyLessonsPage(payload);
        const items = Array.isArray(result?.items) ? result.items : [];
        if (items.some((l) => String(l?.id) === String(id))) { foundPage = p; break; }
        if (items.length < pageSize || p * pageSize >= Number(result?.total || 0)) break;
      }
    } catch (e) {
      console.warn("[OnlineClass] could not locate created lesson:", e);
    }
    if (foundPage == null) return { id };

    const willReset = active !== tab || hidden;
    if (hidden) clearRange(tab);
    if (active !== tab) setActive(tab);
    if (willReset) pendingPageRef.current = foundPage;
    else setPage(foundPage);
    return { id };
  };

  const handleLessonModalSuccess = async (created) => {
    const wasCreate = modalMode === "create";
    setIsLessonModalOpen(false);
    setEditingLesson(null);
    setModalMode("create");

    if (wasCreate) {
      const { id } = await locateCreatedLesson(created);
      pendingFocusRef.current = {
        prevIds: new Set((pageItems || []).map((l) => String(l?.id))),
        explicitId: id,
        started: false,
      };
    }
    reloadPage();
  };

  const handleShowRecording = async (lesson) => {
    try {
      let recordings = Array.isArray(lesson?.recordings)
        ? lesson.recordings.filter((r) => r?.recordingUrl)
        : [];

      if (!recordings.length && lesson?.id) {
        recordings = await fetchSavedLessonRecordings(lesson.id);
      }

      if (!recordings.length && lesson?.recordingUrl) {
        recordings = [
          {
            id: `${lesson.id}-fallback`,
            recordingUrl: lesson.recordingUrl,
          },
        ];
      }

      if (!recordings.length) {
        showAlert?.("לא נמצאו הקלטות לשיעור זה.", "warning");
        return;
      }

      // markLessonRecordingWatched sets lesson.canRate=true, which is
      // exactly the flag that makes a rating UI eligible downstream —
      // so this gate is what actually keeps rating student-only. Since
      // effectiveRole is 'student' for a supplier too (kept that way
      // for canJoin/LessonCard compatibility), excluding isSupplier
      // here explicitly is required, not just for symmetry.
      if (effectiveRole === "student" && !isSupplier && lesson?.id) {
        try {
          await markLessonRecordingWatched(lesson.id);
          reloadPage();
        } catch (err) {
          console.warn("Failed to mark recording as watched", err);
        }
      }

      setSelectedLessonTitle(
        lesson?.title || lesson?.courseTitle || "הקלטות שיעור"
      );
      setSelectedRecordings(recordings);
      setRecordingsModalOpen(true);
    } catch (err) {
      showAlert?.(err?.message || "שגיאה בעת טעינת ההקלטות.", "error");
    }
  };

  const canEditLesson = (lesson) => {
    if (!isTeacher) return false;

    // 1. Only group lessons
    const type = String(lesson?.type || "").toLowerCase();
    if (type !== "group" && type !== "קבוצתי") return false;

    // 2. Must be future lesson
    if (!lesson?.date || !lesson?.startTime) return false;

    const now = new Date();
    const lessonStart = new Date(`${lesson.date}T${lesson.startTime}`);

    // 3. At least 1 hour before start
    const oneHourBefore = new Date(lessonStart.getTime() - 60 * 60 * 1000);

    if (now >= oneHourBefore) return false;

    return true;
  };

  return (
    <>
      <div className="md:min-h-screen bg-gray-100" dir="rtl">
        {course ? <CourseInfo course={course} /> : <OnlineClassHeader tone="amber" />}

        <div className="mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8 pt-4 space-y-2">
          <div className="pt-0 px-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex overflow-hidden rounded-lg bg-gray-50 p-1">
                  {TAB_LIST.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActive(key)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                        active === key
                          ? "bg-white text-indigo-600 shadow-sm"
                          : "text-gray-700 hover:text-gray-900"
                      }`}
                    >
                      {label} ({counts[key] || 0})
                    </button>
                  ))}
                </div>

                {canShowCreateButton && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!canCreateLesson) return;
                      setModalMode("create");
                      setEditingLesson(null);
                      setIsLessonModalOpen(true);
                    }}
                    disabled={!canCreateLesson}
                    title={
                      canCreateLesson
                        ? "מפגש חדש"
                        : "לא ניתן ליצור מפגש עד לאישור המורה"
                    }
                    className={[
                      "shrink-0 inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      canCreateLesson
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "bg-gray-300 text-gray-600 cursor-not-allowed",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full",
                        canCreateLesson ? "bg-white/15" : "bg-black/10",
                      ].join(" ")}
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 4v16M20 12H4"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    מפגש חדש
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <DateRangePicker
                  onDateRangeChange={(s, e) => setRange(active, s, e)}
                  minDate={-3650}
                  hasRange={hasRange}
                  onClear={() => clearRange(active)}
                />
              </div>
            </div>
          </div>

          {hasRange && (
            <div className="flex items-center gap-2 flex-shrink-0 px-2">
              <span className="text-sm text-gray-500">
                {TAB_LIST.find((t) => t.key === active)?.label} • בטווח שנבחר
              </span>

              <span className="inline-flex items-center justify-center rounded-full bg-gray-100 text-gray-700 text-xs font-bold px-2.5 py-1 min-w-[28px]">
                {pageTotal}
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-600">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                סינון פעיל
              </span>
            </div>
          )}

          <div className="pt-4">
            {pageLoading ? (
              <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />
            ) : listForTab.length === 0 ? (
              <div className="flex flex-col items-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 justify-center py-16 text-center">
                <div className="mb-4 w-16 h-16 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center">
                  <svg className="h-7 w-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253"
                    />
                  </svg>
                </div>

                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {EMPTY_MSG[active]}
                </h3>

                <p className="text-sm text-gray-500 max-w-sm">
                  {hasRange
                    ? "לא נמצאו מפגשים בטווח התאריכים שנבחר."
                    : courseId
                    ? "נסה לשנות את הסינון או לחזור מאוחר יותר."
                    : isTeacher || isSupplier
                    ? "לא נמצאו מפגשים בקורסים שלך."
                    : "לא נמצאו מפגשים בקורסים אליהם נרשמת."}
                </p>
              </div>
            ) : (
              <>
                 {totalPages > 1 && (
                    <div className="flex items-center justify-between mb-3.5 px-3.5">
                      <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-xs">
                        עמוד {page} מתוך {totalPages}
                      </span>
                    </div>
                  )}

                <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(260px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] xl:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4 sm:gap-5 md:gap-6">
                  {listForTab.map((lesson) => {
                    const lCourse =
                      (allCourses || []).find(
                        (c) => String(c.id) === String(lesson.courseId)
                      ) ||
                      course ||
                      null;

                    // A supplier attending their own course's lesson never
                    // needs to pay for it, but still can't join before the
                    // lesson is actually live — canJoin(lesson, 'student')
                    // requires lesson.purchased AND state==='STARTABLE'
                    // (confirmed from LessonsContext.js), so the bypass
                    // below only replaces the purchased check, checking
                    // STARTABLE independently via getRuntimeState — the
                    // same treatment canJoin already gives a 'teacher'.
                    // Scoped to courses this exact supplier created,
                    // never another supplier's course.
                    const courseOwnerId =
                      lCourse?.createdByUserId ??
                      lCourse?.created_by_user_id ??
                      null;
                    const isOwnSupplierCourse =
                      isSupplier &&
                      courseOwnerId != null &&
                      String(courseOwnerId) === String(user?.id ?? "");

                    const teacherFromCourse = buildTeacherFromCourse(lCourse);

                    const teacherFromUser = isTeacher
                      ? {
                          id: user?.id ?? null,
                          firstName: user?.first_name || user?.firstName || "",
                          lastName: user?.last_name || user?.lastName || "",
                          name: user?.name || user?.fullName || "",
                          pictureUrl:
                            user?.pictureUrl ||
                            user?.profileImage ||
                            user?.profile_image ||
                            user?.avatar ||
                            user?.photo ||
                            user?.image ||
                            null,
                        }
                      : null;

                    const isFocused = String(focusLessonId) === String(lesson.id);

                    return (
                      <div
                        key={lesson.id}
                        id={`lesson-card-${lesson.id}`}
                        tabIndex={-1}
                        className={`h-full rounded-2xl outline-none transition-shadow duration-500 ${
                          isFocused ? "ring-4 ring-indigo-400 ring-offset-2 shadow-xl" : ""
                        }`}
                      >
                      <LessonCard
                        lesson={lesson}
                        course={lCourse}
                        teacherFallback={teacherFromCourse || teacherFromUser || null}
                        role={effectiveRole}
                        canJoin={isOwnSupplierCourse ? getRuntimeState(lesson) === 'STARTABLE' : canJoin(lesson, effectiveRole)}
                        isInCart={isInCart(lesson.id)}
                        onAddToCart={() =>
                          addLessonFromOnline(lesson, lCourse || course)
                        }
                        onShowRecording={handleShowRecording}
                        canEdit={canEditLesson(lesson)}
                        onRequestEdit={canEditLesson(lesson) ? handleRequestEdit : undefined}
                        onShowParticipants={
                          isTeacher || isSupplier
                            ? (lsn) => handleShowParticipants(lsn, lCourse || course)
                            : undefined
                        }
                        onCancelSession={isTeacher ? handleCancelSession : undefined}
                      />
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-start mt-5 mb-4">
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={(nextPage) => {
                      if (
                        nextPage < 1 ||
                        nextPage > totalPages ||
                        nextPage === page
                      ) {
                        return;
                      }
                      setPage(nextPage);
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isTeacher && (
        <CreateLessonModal
          isOpen={isLessonModalOpen}
          onClose={handleLessonModalClose}
          onSuccess={handleLessonModalSuccess}
          courseId={
            editingLesson?.courseId ||
            editingLesson?.course_id ||
            courseId
          }
          courseTitle={
            editingLesson?.courseTitle ||
            editingLesson?.course_title ||
            course?.title
          }
          teacherId={
            course?.teacher?.id || 
            course?.teacherId ||
            course?.teacher_id ||
            user?.id
          }
          defaultPlatform="ZOOM"
          mode={modalMode}
          lesson={editingLesson}
        />
      )}

      {(isTeacher || isSupplier) && (
        <LessonParticipantsReportModal
          isOpen={isReportOpen}
          onClose={() => {
            setIsReportOpen(false);
            setReportLesson(null);
          }}
          lesson={reportLesson}
        />
      )}

      {isTeacher && (
        <ConfirmDialog
          isOpen={isCancelDialogOpen}
          onClose={() => {
            setIsCancelDialogOpen(false);
            setLessonToCancel(null);
          }}
          onConfirm={handleConfirmCancel}
          title="ביטול מפגש"
          body={
            lessonToCancel
              ? `האם אתה בטוח שברצונך לבטל את המפגש:\n"${lessonToCancel.title}"\nבתאריך ${lessonToCancel.date} בשעה ${lessonToCancel.startTime}?`
              : "האם אתה בטוח שברצונך לבטל את המפגש?"
          }
          confirmText="בטל מפגש"
          cancelText="חזור"
          danger
        />
      )}

      <RecordingsPlaylistModal
        isOpen={recordingsModalOpen}
        onClose={() => {
          setRecordingsModalOpen(false);
          setSelectedRecordings([]);
          setSelectedLessonTitle("");
        }}
        lessonTitle={selectedLessonTitle}
        recordings={selectedRecordings}
      />
    </>
  );
}